/**
 * Wall-geometry helpers shared by ElevationViews + WallPanel.
 * Pure functions only — no React, no DOM.
 */

export const genId = () => {
  if (typeof crypto !== "undefined" && crypto.randomUUID)
    return crypto.randomUUID();
  return `id-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
};

export const WALLS = [
  { id: "A", label: "Wall A · back",  axis: "x", at: "yMax" },
  { id: "B", label: "Wall B · right", axis: "y", at: "xMax" },
  { id: "C", label: "Wall C · front", axis: "x", at: "yMin" },
  { id: "D", label: "Wall D · left",  axis: "y", at: "xMin" },
];

export const WALL_KEY_TO_ID = { back: "A", right: "B", front: "C", left: "D" };

/** Map a (wallId, offset along wall) pair → plan-view (x, y) inches. */
export function wallToPlan(wallId, offset, L, W, eps) {
  switch (wallId) {
    case "A": return [Math.min(L - eps, Math.max(eps, offset)), W - eps];
    case "B": return [L - eps, Math.min(W - eps, Math.max(eps, offset))];
    case "C": return [Math.min(L - eps, Math.max(eps, offset)), eps];
    case "D": return [eps, Math.min(W - eps, Math.max(eps, offset))];
    default:  return [offset, 0];
  }
}

/** Inches between the tray centerline and the nearest wall for a tray
 *  mounted flush against that wall. tray_w is the rail-to-rail span.
 *  Adds half of `rail_w` (the C-channel rail thickness in 3D) so the
 *  outer rail edge sits exactly at the wall instead of overshooting it.
 *  v1.78 — fixes "Wall B tray renders outside the enclosure in 3D". */
export const TRAY_RAIL_THICKNESS_IN = 1.0;
export function wallFlushMargin(tray_w_in, rail_w_in = TRAY_RAIL_THICKNESS_IN) {
  return (tray_w_in / 2) + (rail_w_in / 2);
}

/** Pick the closest wall to a plan-view (x, y) point (within nearLimit). */
export function planToWall(x, y, L, W, nearLimit = 12) {
  const candidates = [
    { id: "A", dist: W - y, offset: x },
    { id: "B", dist: L - x, offset: y },
    { id: "C", dist: y,     offset: x },
    { id: "D", dist: x,     offset: y },
  ];
  candidates.sort((a, b) => a.dist - b.dist);
  return candidates[0].dist <= nearLimit ? candidates[0] : null;
}

/** Compute the plan-view rect (x_in / y_in / w_in / h_in) for placed
 *  equipment given the wall it's mounted on + offset + L×W footprint. */
export function positionForWall(wallId, offsetAlongWall, length_in, width_in, L, W) {
  const length = length_in || 24;
  const width  = width_in  || 18;
  const halfLen = length / 2;
  switch (wallId) {
    case "A": {
      const x = Math.max(0, Math.min(L - length, offsetAlongWall - halfLen));
      return { x_in: x, y_in: Math.max(0, W - width), w_in: length, h_in: width };
    }
    case "B": {
      const y = Math.max(0, Math.min(W - length, offsetAlongWall - halfLen));
      return { x_in: Math.max(0, L - width), y_in: y, w_in: width, h_in: length };
    }
    case "C": {
      const x = Math.max(0, Math.min(L - length, offsetAlongWall - halfLen));
      return { x_in: x, y_in: 0, w_in: length, h_in: width };
    }
    case "D":
    default: {
      const y = Math.max(0, Math.min(W - length, offsetAlongWall - halfLen));
      return { x_in: 0, y_in: y, w_in: width, h_in: length };
    }
  }
}
